import React, { useEffect, useState } from 'react';
import { Briefcase, Award, Calendar, Wrench, Flame, Users, BookOpen, Settings as SettingsIcon, X, ChevronRight } from '../../components/CustomIcons';
import { Btn, Chip, Mono, ProgressBar, ScreenHeader } from '../Prototype';
import { useUI } from './shared';

/* Storage helpers */
const readLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    if (raw.startsWith('[') || raw.startsWith('{')) return JSON.parse(raw);
    if (!Number.isNaN(Number(raw)) && raw.trim() !== '') return Number(raw);
    return raw;
  } catch (_e) { return fallback; }
};
const writeLS = (key, value) => {
  try {
    localStorage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
  } catch (_e) { /* ignore */ }
};

/* ==========================================================
   IMPROVEMENT 1: Goal card selection (Screen 1C)
   ========================================================== */

const GOALS = [
  { key: 'job', icon: Briefcase, label: 'Get a job after release', desc: 'Connect your learning to real employer opportunities.' },
  { key: 'certification', icon: Award, label: 'Earn a certification', desc: 'Build credentials that transfer beyond these walls.' },
  { key: 'release', icon: Calendar, label: 'Prepare for release', desc: 'Build a plan and resources for what comes next.' },
  { key: 'trade', icon: Wrench, label: 'Learn a skilled trade', desc: 'Master a hands-on career with strong demand.' },
];

export const GOAL_LABELS = Object.fromEntries(GOALS.map((g) => [g.key, g.label]));

export const GoalScreen = ({ onContinue, onBack }) => {
  const [selected, setSelected] = useState(readLS('assistivos_learner_goal', null));
  const save = () => {
    writeLS('assistivos_learner_goal', selected);
    onContinue();
  };
  return (
    <>
      <ScreenHeader
        title="What matters most right now?"
        subtitle="Personalize your journey"
        onBack={onBack}
        breadcrumbs={[{ label: 'Modules', onClick: onBack }, { label: 'Your Goal' }]}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <p className="text-[14px] text-[#64748b]">Choose one. You can change this later.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {GOALS.map((g) => {
            const active = selected === g.key;
            const Icon = g.icon;
            return (
              <button
                key={g.key}
                onClick={() => setSelected(g.key)}
                data-testid={`proto-goal-${g.key}`}
                aria-pressed={active}
                className={`text-left rounded-[12px] p-4 transition-all ${active ? 'p-card-active' : 'bg-[#f1f5f9] border border-transparent hover:bg-white hover:border-[#cbd5e1]'}`}
                style={{ minHeight: 130 }}
              >
                <div className="w-10 h-10 rounded-[10px] flex items-center justify-center mb-3" style={{ background: active ? '#f97316' : '#e2e8f0', color: active ? '#0f2744' : '#64748b' }}>
                  <Icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <div className="p-display text-[16px] leading-tight" style={{ color: '#0f2744' }}>{g.label}</div>
                <div className="text-[13px] text-[#64748b] mt-1 leading-snug">{g.desc}</div>
              </button>
            );
          })}
        </div>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn onClick={save} disabled={!selected} className={`w-full ${!selected ? 'opacity-40 pointer-events-none' : ''}`} data-testid="proto-goal-continue">
          Continue
        </Btn>
      </footer>
    </>
  );
};

/* ==========================================================
   IMPROVEMENT 3: Engagement layer (streak/XP + daily challenge + cohort)
   ========================================================== */

const CHALLENGE_QUESTIONS = [
  { q: 'What is the correct order of the refrigeration cycle?', opts: ['Compress → Condense → Expand → Evaporate', 'Evaporate → Condense → Compress → Expand', 'Expand → Compress → Condense → Evaporate'], correct: 0 },
  { q: 'What certification is federally required to handle refrigerants?', opts: ['OSHA 30', 'EPA 608', 'NABCEP Associate'], correct: 1 },
  { q: 'Standard HVAC thermostat control voltage is:', opts: ['12V DC', '24V AC', '120V AC'], correct: 1 },
];

export const EngagementLayer = ({ onOpenChallenge }) => {
  const streak = readLS('assistivos_streak', 7);
  const xp = readLS('assistivos_xp', 1240);
  const answered = readLS('assistivos_challenge_answers', []);
  const toNext = 360;
  const xpPct = ((xp % 500) / 500) * 100;
  return (
    <section aria-label="Your engagement" className="space-y-3">
      {/* Streak + XP */}
      <div className="rounded-[12px] bg-[#f1f5f9] p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4" style={{ color: '#f97316' }} aria-hidden="true" />
            <span className="text-[13px] font-semibold" style={{ color: '#0f2744', fontFamily: 'var(--p-font-mono)' }}>
              <Mono>{streak}</Mono>-day streak
            </span>
          </div>
          <span className="text-[13px] font-semibold" style={{ color: '#0f2744', fontFamily: 'var(--p-font-mono)' }}>
            <Mono>{xp.toLocaleString()}</Mono> XP
          </span>
        </div>
        <div className="mt-2"><ProgressBar value={xpPct} ariaLabel={`${xp} experience points, ${toNext} to next level`} /></div>
        <div className="mt-1 text-[12px] text-[#64748b]"><Mono>{toNext}</Mono> XP to next level</div>
      </div>

      {/* Daily challenge */}
      <button
        onClick={onOpenChallenge}
        data-testid="proto-daily-challenge"
        className="w-full text-left rounded-[12px] bg-white p-4 transition-all hover:shadow-md"
        style={{ borderTop: '3px solid #f97316', border: '1px solid #e2e8f0', borderTopWidth: 3, borderTopColor: '#f97316' }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="p-display text-[16px] leading-tight" style={{ color: '#0f2744' }}>5-Minute Trade Trivia</span>
              <span className="rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5" style={{ background: 'rgba(21,128,61,0.12)', color: '#15803d' }}>+50 XP</span>
            </div>
            <div className="text-[13px] text-[#64748b] mt-1">Answer 3 questions about HVAC fundamentals</div>
            <div className="text-[12px] text-[#64748b] mt-1"><Mono>{answered.length}</Mono> of <Mono>3</Mono> questions answered</div>
          </div>
          <span className="rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 whitespace-nowrap" style={{ background: '#f97316', color: '#0f2744' }}>Today&apos;s Challenge</span>
        </div>
        <div className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[#c2410c]">Start Challenge <ChevronRight className="w-4 h-4" aria-hidden="true" /></div>
      </button>

      {/* Cohort banner */}
      <div className="flex items-center gap-2 text-[13px]" style={{ color: '#64748b' }}>
        <Users className="w-4 h-4 shrink-0" style={{ color: '#f97316' }} aria-hidden="true" />
        <span><Mono>43</Mono> learners at your facility are working toward credentials this week.</span>
      </div>
    </section>
  );
};

export const DailyChallengeModal = ({ onClose }) => {
  const { showToast } = useUI();
  const [idx, setIdx] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [complete, setComplete] = useState(false);
  const q = CHALLENGE_QUESTIONS[idx];
  const choose = (i) => {
    if (chosen !== null) return;
    setChosen(i);
    setTimeout(() => {
      if (idx < CHALLENGE_QUESTIONS.length - 1) {
        setIdx(idx + 1);
        setChosen(null);
      } else {
        const xp = readLS('assistivos_xp', 1240);
        writeLS('assistivos_xp', xp + 50);
        writeLS('assistivos_challenge_answers', [0, 1, 2]);
        setComplete(true);
      }
    }, 900);
  };
  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center px-6" role="dialog" aria-modal="true" aria-label="Daily challenge">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-[12px] p-5" style={{ background: '#fafaf9' }}>
        {complete ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3" style={{ background: '#f97316' }}>
              <Flame className="w-7 h-7" style={{ color: '#0f2744' }} />
            </div>
            <h2 className="p-display text-[20px]" style={{ color: '#0f2744', margin: 0 }}>+50 XP earned!</h2>
            <p className="text-[14px] text-[#64748b] mt-2">Come back tomorrow for a new challenge.</p>
            <Btn onClick={() => { onClose(); showToast('Streak extended. See you tomorrow.', 'success'); }} className="w-full mt-4" data-testid="proto-challenge-done">Done</Btn>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#f97316' }}>Question <Mono>{idx + 1}</Mono> of <Mono>{CHALLENGE_QUESTIONS.length}</Mono></span>
              <button onClick={onClose} className="text-[#64748b]" aria-label="Close challenge" data-testid="proto-challenge-close"><X className="w-4 h-4" /></button>
            </div>
            <h2 className="p-display text-[18px] leading-tight" style={{ color: '#0f2744', margin: 0 }}>{q.q}</h2>
            <div className="mt-4 space-y-2">
              {q.opts.map((opt, i) => {
                const isChosen = chosen === i;
                const isCorrect = i === q.correct;
                let bg = '#f1f5f9', border = 'transparent', color = '#1c1c1e';
                if (chosen !== null) {
                  if (isCorrect) { bg = 'rgba(21,128,61,0.10)'; border = '#15803d'; color = '#0f2744'; }
                  else if (isChosen) { bg = 'rgba(220,38,38,0.08)'; border = '#dc2626'; color = '#7f1d1d'; }
                }
                return (
                  <button
                    key={i}
                    onClick={() => choose(i)}
                    disabled={chosen !== null}
                    data-testid={`proto-challenge-answer-${i}`}
                    className="w-full text-left px-4 py-3 rounded-[12px] border transition-all text-[14px] min-h-[48px]"
                    style={{ background: bg, borderColor: border, color }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ==========================================================
   IMPROVEMENT 2: First-time spotlight tutorial (3 steps)
   ========================================================== */

const TUTORIAL_STEPS = [
  { title: 'Your learning journey', body: 'The pathway rail fills as you complete lessons and earn credentials.' },
  { title: 'Your recommended modules', body: 'These pathways are personalized to your goal and career path.' },
  { title: 'Your credentials', body: 'Every hour here moves you closer to a nationally recognized qualification.' },
];

export const SpotlightTutorial = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const isLast = step === TUTORIAL_STEPS.length - 1;
  const finish = () => {
    writeLS('assistivos_tutorial_complete', true);
    onComplete();
  };
  const cur = TUTORIAL_STEPS[step];
  return (
    <div className="absolute inset-0 z-40" role="dialog" aria-modal="true" aria-label="Tutorial" data-testid="proto-tutorial">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.65)' }} />
      <div className="absolute inset-x-0 bottom-0 p-5">
        <div className="rounded-[12px] p-5" style={{ background: '#0f2744', color: '#fafaf9' }}>
          <div className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: '#f97316' }}>Step {step + 1} of {TUTORIAL_STEPS.length}</div>
          <h2 className="p-display text-[20px] leading-tight" style={{ color: '#fafaf9', margin: 0 }}>{cur.title}</h2>
          <p className="text-[14px] mt-2 leading-relaxed" style={{ color: 'rgba(250,250,249,0.85)' }}>{cur.body}</p>
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5" aria-hidden="true">
              {TUTORIAL_STEPS.map((_, i) => (
                <div key={i} className="w-2 h-2 rounded-full" style={{ background: i === step ? '#f97316' : 'rgba(255,255,255,0.25)' }} />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={finish} className="text-[13px] font-semibold" style={{ color: 'rgba(250,250,249,0.6)' }} data-testid="proto-tutorial-skip">
                Skip tutorial
              </button>
              <Btn onClick={() => (isLast ? finish() : setStep(step + 1))} data-testid="proto-tutorial-next">
                {isLast ? 'Start learning' : 'Next'}
              </Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================================================
   IMPROVEMENT 6: Mood check-in (used on Module Overview)
   ========================================================== */

const MOODS = [
  { key: 'rough', emoji: '😞', label: 'Rough day', response: 'Learning something new can help. Take it at your own pace today.' },
  { key: 'unsure', emoji: '😕', label: 'Unsure', response: "That's okay. Start with one lesson and see how it feels." },
  { key: 'okay', emoji: '😐', label: 'Okay', response: 'A steady day is a good day to build a skill.' },
  { key: 'good', emoji: '🙂', label: 'Good', response: "Good momentum. Let's use it." },
  { key: 'ready', emoji: '😄', label: 'Ready', response: "Let's go. Your credential is waiting for you." },
];

export const MoodCheckin = ({ moduleId }) => {
  const [mood, setMood] = useState(() => {
    const stored = readLS('assistivos_moods', {});
    return stored[moduleId] || null;
  });
  const [showResponse, setShowResponse] = useState(!!mood);
  const pick = (m) => {
    const stored = readLS('assistivos_moods', {});
    stored[moduleId] = m.key;
    writeLS('assistivos_moods', stored);
    setMood(m.key);
    setShowResponse(true);
  };
  const active = MOODS.find((m) => m.key === mood);
  return (
    <section aria-label="Mood check-in" className="border-t border-[#e2e8f0] pt-4 mt-2">
      <div className="text-[14px]" style={{ color: '#64748b' }}>How are you feeling today?</div>
      {!showResponse ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {MOODS.map((m) => (
            <button
              key={m.key}
              onClick={() => pick(m)}
              data-testid={`proto-mood-${m.key}`}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-semibold border transition-all min-h-[40px]"
              style={{ background: '#f1f5f9', borderColor: 'transparent', color: '#0f2744' }}
            >
              <span aria-hidden="true">{m.emoji}</span> {m.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-3 rounded-[12px] p-3" style={{ background: 'rgba(249,115,22,0.08)' }} role="status" aria-live="polite">
          <p className="text-[13px] italic" style={{ color: '#c2410c' }}>{active?.response}</p>
          <button onClick={() => setShowResponse(false)} className="text-[12px] font-semibold text-[#64748b] hover:underline mt-1" data-testid="proto-mood-change">Change</button>
        </div>
      )}
    </section>
  );
};

/* ==========================================================
   IMPROVEMENT 5: Post-lesson reflection micro-journal
   ========================================================== */

export const ReflectionInput = ({ moduleId, lessonIdx, lessonTitle }) => {
  const { showToast } = useUI();
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);
  const save = () => {
    if (!text.trim()) return;
    const entries = readLS('assistivos_journal_entries', []);
    entries.unshift({ text: text.trim(), moduleId, lessonIdx, lessonTitle, at: new Date().toISOString() });
    writeLS('assistivos_journal_entries', entries);
    showToast('Saved to your journal.', 'success');
    setSaved(true);
  };
  if (saved) {
    return (
      <div className="border-t border-[#e2e8f0] pt-4 text-center">
        <p className="text-[13px] text-[#15803d] italic">Saved to your journal ✓</p>
      </div>
    );
  }
  return (
    <div className="border-t border-[#e2e8f0] pt-4">
      <div className="text-[14px] font-semibold text-[#64748b]">Take 30 seconds to reflect</div>
      <p className="text-[13px] italic text-[#64748b] mt-1">In one sentence, what will you use from this lesson?</p>
      <div className="mt-2 flex gap-2 items-start">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 120))}
          maxLength={120}
          placeholder="e.g., I'll double-check my BP cuff size on every reading."
          className="flex-1 min-h-[44px] rounded-[10px] border border-[#e2e8f0] bg-white px-3 text-[13px] focus:outline-none focus:border-[#f97316]"
          data-testid="proto-reflection-input"
          aria-label="Reflection input"
        />
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button
          onClick={save}
          disabled={!text.trim()}
          data-testid="proto-reflection-save"
          className="rounded-[10px] px-4 py-2 text-[13px] font-semibold"
          style={{ background: text.trim() ? '#f1f5f9' : 'transparent', color: text.trim() ? '#0f2744' : '#94a3b8', border: '1px solid #e2e8f0' }}
        >
          Save to journal
        </button>
        <button onClick={() => setSaved(true)} className="text-[13px] font-semibold text-[#64748b] hover:underline" data-testid="proto-reflection-skip">Skip</button>
        <span className="ml-auto text-[11px] text-[#94a3b8]"><Mono>{text.length}</Mono>/120</span>
      </div>
    </div>
  );
};

export const JournalView = ({ onBack }) => {
  const entries = readLS('assistivos_journal_entries', []);
  return (
    <>
      <ScreenHeader title="My Journal" subtitle={`${entries.length} entries`} onBack={onBack} breadcrumbs={[{ label: 'Modules', onClick: onBack }, { label: 'Journal' }]} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
        {entries.length === 0 ? (
          <div className="text-center text-[14px] text-[#64748b] py-8">
            No reflections yet. Save one at the end of a lesson to build your journal.
          </div>
        ) : (
          entries.map((e, i) => (
            <div key={i} className="rounded-[12px] bg-[#f1f5f9] p-4">
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#64748b]">{new Date(e.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</div>
              <div className="text-[15px] text-[#0f2744] mt-1 leading-relaxed">{e.text}</div>
              <div className="text-[12px] text-[#64748b] mt-2">{e.lessonTitle}</div>
            </div>
          ))
        )}
      </main>
    </>
  );
};

/* ==========================================================
   IMPROVEMENT 7: Accessibility bottom sheet
   ========================================================== */

export const SettingsButton = ({ onOpen }) => (
  <button
    onClick={onOpen}
    data-testid="proto-settings-btn"
    className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#f1f5f9]"
    style={{ color: '#64748b' }}
    aria-label="Open display settings"
  >
    <SettingsIcon className="w-4 h-4" aria-hidden="true" />
  </button>
);

const SettingsRow = ({ title, subtitle, value, onChange, testid }) => (
  <div className="flex items-start justify-between gap-3 py-4 border-b border-[#e2e8f0]">
    <div className="flex-1 min-w-0">
      <div className="text-[15px] font-semibold" style={{ color: '#0f2744' }}>{title}</div>
      <div className="text-[13px] text-[#64748b] mt-0.5 leading-snug">{subtitle}</div>
    </div>
    <button
      onClick={() => onChange(!value)}
      data-testid={testid}
      role="switch"
      aria-checked={value}
      className="relative w-12 h-7 rounded-full transition-all shrink-0"
      style={{ background: value ? '#f97316' : '#cbd5e1' }}
    >
      <span
        className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all"
        style={{ left: value ? 22 : 4 }}
      />
    </button>
  </div>
);

export const SettingsSheet = ({ onClose }) => {
  const [plain, setPlain] = useState(readLS('assistivos_plain_language', false));
  const [large, setLarge] = useState(readLS('assistivos_large_text', false));
  const toggle = (key, val, setter) => {
    setter(val);
    writeLS(key, val);
    if (key === 'assistivos_large_text') {
      document.querySelector('.proto-root')?.setAttribute('data-large-text', val ? 'true' : 'false');
    }
  };
  useEffect(() => {
    document.querySelector('.proto-root')?.setAttribute('data-large-text', large ? 'true' : 'false');
  }, [large]);
  return (
    <div className="absolute inset-0 z-40" role="dialog" aria-modal="true" aria-label="Display settings" data-testid="proto-settings-sheet">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={onClose} />
      <div
        className="absolute inset-x-0 bottom-0 rounded-t-[16px] p-5"
        style={{ background: '#fafaf9', maxHeight: '65%', overflowY: 'auto', animation: 'proto-fade-up 220ms ease-out both' }}
      >
        <div className="w-10 h-1 rounded-full bg-[#cbd5e1] mx-auto mb-4" aria-hidden="true" />
        <h2 className="p-display text-[20px]" style={{ color: '#0f2744', margin: 0 }}>Display Settings</h2>
        <p className="text-[13px] text-[#64748b] mt-1">Adjust these to make the app easier to read.</p>
        <div className="mt-4">
          <SettingsRow title="Plain language mode" subtitle="Simplifies vocabulary in lesson content." value={plain} onChange={(v) => toggle('assistivos_plain_language', v, setPlain)} testid="proto-toggle-plain" />
          <SettingsRow title="Larger text" subtitle="Increases text size throughout the app." value={large} onChange={(v) => toggle('assistivos_large_text', v, setLarge)} testid="proto-toggle-large" />
        </div>
        <Btn onClick={onClose} className="w-full mt-4" data-testid="proto-settings-done">Done</Btn>
      </div>
    </div>
  );
};

/* ==========================================================
   IMPROVEMENT 1 (badge on home): Goal badge
   ========================================================== */

export const GoalBadge = ({ onDismiss }) => {
  const goal = readLS('assistivos_learner_goal', null);
  const [dismissed, setDismissed] = useState(readLS('assistivos_goal_badge_dismissed', false));
  if (!goal || dismissed) return null;
  const label = GOAL_LABELS[goal];
  return (
    <div className="flex items-center gap-2 rounded-[10px] bg-[rgba(249,115,22,0.08)] border border-[rgba(249,115,22,0.30)] px-3 py-2" data-testid="proto-goal-badge">
      <span className="text-[12px] text-[#64748b] font-semibold uppercase tracking-widest">Your goal:</span>
      <span className="text-[13px] font-semibold text-[#0f2744] flex-1 min-w-0 truncate">{label}</span>
      <button
        onClick={() => { writeLS('assistivos_goal_badge_dismissed', true); setDismissed(true); onDismiss && onDismiss(); }}
        className="w-6 h-6 rounded-full flex items-center justify-center text-[#64748b] hover:bg-white"
        aria-label="Dismiss goal reminder"
        data-testid="proto-goal-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

/* Book icon link to journal */
export const JournalLink = ({ onOpen }) => (
  <button
    onClick={onOpen}
    data-testid="proto-journal-link"
    className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#64748b] hover:text-[#0f2744] px-3 py-2 rounded-[8px]"
    aria-label="Open my journal"
  >
    <BookOpen className="w-4 h-4" aria-hidden="true" /> My Journal
  </button>
);
